import React, { useState, useEffect } from 'react';
import { Briefcase, Users, CalendarCheck, DoorOpen } from 'lucide-react';
import { getOperationalStats } from '../../services/api';

const formatCurrency = (a) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(a);
const TYPE_LABELS = { vaccine: 'Aşı', treatment: 'Tedavi', service: 'Hizmet', product: 'Ürün' };
const TYPE_COLORS = { vaccine: 'bg-[#34C759]', treatment: 'bg-[#6b6de3]', service: 'bg-[#FF9500]', product: 'bg-[#AF52DE]' };
const STATUS_LABELS = { pending: 'Bekliyor', informed: 'Bilgilendirildi', completed: 'Tamamlandı', cancelled: 'İptal', approved: 'Onaylandı', rejected: 'Reddedildi' };

export default function OperationalReport({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getOperationalStats(dateFrom, dateTo).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  if (loading) return <div className="py-8 flex justify-center"><div className="w-5 h-5 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const topMax = Math.max(...(data.top_operations?.map(o => o.count) || [1]), 1);
  const staffMax = Math.max(...(data.staff_performance?.map(s => s.total) || [1]), 1);

  return (
    <div className="space-y-3">
      {/* Top Operations */}
      {data.top_operations?.length > 0 && (
        <div className="ios-card">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase size={14} className="text-[#6b6de3]" />
            <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">En Çok Yapılan İşlemler</p>
          </div>
          <div className="space-y-1.5">
            {data.top_operations.map((op, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-[#6b6de3]/10 text-[#6b6de3] text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${TYPE_COLORS[op.type] || 'bg-gray-400'}`} />
                    <span className="text-xs font-medium text-gray-900 truncate">{op.name}</span>
                  </div>
                  <div className="flex-1 bg-[#F2F2F7] rounded-full h-2.5 mt-0.5 overflow-hidden">
                    <div className={`h-full rounded-full ${TYPE_COLORS[op.type] || 'bg-gray-400'} transition-all`} style={{ width: `${(op.count / topMax) * 100}%` }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] font-bold text-gray-900">{op.count}x</p>
                  <p className="text-[9px] text-[#FF9500]">{formatCurrency(op.total)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Staff Performance */}
      {data.staff_performance?.length > 0 && (
        <div className="ios-card">
          <div className="flex items-center gap-2 mb-3">
            <Users size={14} className="text-[#34C759]" />
            <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Ekip Performansı</p>
          </div>
          <div className="space-y-1.5">
            {data.staff_performance.map((s, i) => (
              <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-[#F2F2F7] rounded-xl">
                <span className="w-6 h-6 rounded-full bg-[#34C759]/10 text-[#34C759] text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{s.name}</p>
                  <div className="flex-1 bg-white rounded-full h-2 mt-0.5 overflow-hidden">
                    <div className="h-full rounded-full bg-[#34C759] transition-all" style={{ width: `${(s.total / staffMax) * 100}%` }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] font-bold text-[#FF9500]">{formatCurrency(s.total)}</p>
                  <p className="text-[9px] text-[#8E8E93]">{s.count} işlem</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Appointment Stats */}
      <div className="ios-card">
        <div className="flex items-center gap-2 mb-3">
          <CalendarCheck size={14} className="text-[#007AFF]" />
          <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Randevu İstatistikleri</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(data.appointment_status || {}).map(([status, count]) => (
            <div key={status} className="bg-[#F2F2F7] rounded-xl px-2.5 py-2 text-center">
              <p className="text-lg font-bold text-gray-900">{count}</p>
              <p className="text-[10px] text-[#8E8E93]">{STATUS_LABELS[status] || status}</p>
            </div>
          ))}
        </div>
        {data.total_bookings > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-[10px] text-[#8E8E93]">Online Talepler</p>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {Object.entries(data.booking_status || {}).map(([status, count]) => (
                <div key={status} className="bg-[#F2F2F7] rounded-xl px-2.5 py-1.5 text-center">
                  <p className="text-sm font-bold text-gray-900">{count}</p>
                  <p className="text-[9px] text-[#8E8E93]">{STATUS_LABELS[status] || status}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Boarding Occupancy */}
      <div className="ios-card">
        <div className="flex items-center gap-2 mb-3">
          <DoorOpen size={14} className="text-[#FF9500]" />
          <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Konaklama Doluluk</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-[#F2F2F7] rounded-xl p-2.5 text-center">
            <p className="text-xl font-bold text-[#FF9500]">{data.boarding_occupancy?.active || 0}</p>
            <p className="text-[10px] text-[#8E8E93]">Aktif</p>
          </div>
          <div className="bg-[#F2F2F7] rounded-xl p-2.5 text-center">
            <p className="text-xl font-bold text-gray-900">{data.boarding_occupancy?.total_cages || 0}</p>
            <p className="text-[10px] text-[#8E8E93]">Kafes</p>
          </div>
          <div className="bg-[#F2F2F7] rounded-xl p-2.5 text-center">
            <p className="text-xl font-bold text-gray-900">{data.boarding_occupancy?.total_rooms || 0}</p>
            <p className="text-[10px] text-[#8E8E93]">Oda</p>
          </div>
        </div>
      </div>
    </div>
  );
}
